/**
 * AccountSettings.js
 * User account management: profile, password, subscription, deactivation, deletion
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../utils/ThemeSwitcher';
import {
  User, Mail, Lock, Eye, EyeOff, Shield, CreditCard, Trash2,
  Moon, Sun, AlertTriangle, Check, ChevronRight, LogOut, Pause,
  ArrowLeft
} from 'lucide-react';
import logoSource from '../assets/dataquadio.png';
import logoSourceInverted from '../assets/dataquadio_inverted.png';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.dataquadapp.com';

function authHeaders() {
  const token = localStorage.getItem('token');
  const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
  return {
    'Authorization': token ? `Bearer ${token}` : '',
    'user_email': userInfo?.email || '',
    'Content-Type': 'application/json',
  };
}

const PADDLE_ENVIRONMENT = process.env.REACT_APP_PADDLE_ENVIRONMENT || (process.env.NODE_ENV === 'production' ? 'production' : 'sandbox');

const AccountSettings = () => {
  const navigate = useNavigate();
  const { isDarkMode, toggleTheme } = useTheme();

  // Profile state
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState('');
  const [nameLoading, setNameLoading] = useState(false);
  const [nameSuccess, setNameSuccess] = useState(false);

  // Password state
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);
  const [pwMessage, setPwMessage] = useState('');

  // Danger zone state
  const [showDeactivate, setShowDeactivate] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [dangerPassword, setDangerPassword] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [dangerLoading, setDangerLoading] = useState(false);
  const [dangerError, setDangerError] = useState('');

  const [message, setMessage] = useState('');

  const c = {
    bg: isDarkMode ? '#08090B' : '#FFFFFF',
    bgAlt: isDarkMode ? '#0E1015' : '#F7F8FA',
    bgCard: isDarkMode ? 'rgba(255,255,255,0.03)' : '#FAFAFA',
    border: isDarkMode ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)',
    text: isDarkMode ? '#F0F0F2' : '#111214',
    textSecondary: isDarkMode ? '#8B8D97' : '#6B7280',
    textMuted: isDarkMode ? '#5A5C66' : '#9CA3AF',
    accent: '#2B8AFF',
    accentGradient: 'linear-gradient(135deg, #2B8AFF 0%, #D4A44C 100%)',
    danger: '#EF4444',
    dangerBg: isDarkMode ? 'rgba(239,68,68,0.08)' : 'rgba(239,68,68,0.04)',
    dangerBorder: isDarkMode ? 'rgba(239,68,68,0.2)' : 'rgba(239,68,68,0.12)',
    success: '#10B981',
    inputBg: isDarkMode ? 'rgba(255,255,255,0.04)' : '#FFFFFF',
    inputBorder: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
  };

  // Fetch profile - falls back to localStorage if API unavailable
  const fetchProfile = useCallback(async () => {
    const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
    const fallback = {
      email: userInfo.email || '',
      name: userInfo.name || '',
      picture: userInfo.picture || null,
      is_google_auth: !!userInfo.picture,
      account_status: 'active',
      subscription_status: null,
      subscription_plan: null,
      trial_end_date: null,
      created_at: null,
      project_count: null,
      paddle_customer_id: null,
    };

    try {
      const res = await fetch(`${API_BASE_URL}/api/account/profile`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setProfile(data);
        setNewName(data.name || '');
      } else {
        // API not available yet, use localStorage fallback
        setProfile(fallback);
        setNewName(fallback.name);
      }
    } catch (e) {
      console.error('Failed to fetch profile, using local data:', e);
      setProfile(fallback);
      setNewName(fallback.name);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  // Update name
  const handleUpdateName = async () => {
    setNameLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/account/profile`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ name: newName }),
      });
      if (res.ok) {
        const data = await res.json();
        setProfile(prev => ({ ...prev, name: data.name }));
        // Update localStorage
        const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
        userInfo.name = data.name;
        localStorage.setItem('userInfo', JSON.stringify(userInfo));
        setEditingName(false);
        setNameSuccess(true);
        setTimeout(() => setNameSuccess(false), 2000);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setNameLoading(false);
    }
  };

  // Change password
  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      setPwMessage('Passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      setPwMessage('Password must be at least 8 characters');
      return;
    }
    setPwLoading(true);
    setPwMessage('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/account/change-password`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
      });
      const data = await res.json();
      if (res.ok) {
        setPwMessage('Password changed successfully');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setShowPasswordSection(false);
      } else {
        setPwMessage(data.detail || 'Failed to change password');
      }
    } catch (e) {
      setPwMessage('An error occurred');
    } finally {
      setPwLoading(false);
    }
  };

  // Deactivate
  const handleDeactivate = async () => {
    setDangerLoading(true);
    setDangerError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/account/deactivate`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ password: dangerPassword || null }),
      });
      const data = await res.json();
      if (res.ok) {
        localStorage.clear();
        navigate('/login');
      } else {
        setDangerError(data.detail || 'Failed to deactivate');
      }
    } catch (e) {
      setDangerError('An error occurred');
    } finally {
      setDangerLoading(false);
    }
  };

  // Delete
  const handleDelete = async () => {
    setDangerLoading(true);
    setDangerError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/account/delete`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ password: dangerPassword || null, confirmation: deleteConfirmation }),
      });
      const data = await res.json();
      if (res.ok) {
        localStorage.clear();
        navigate('/');
      } else {
        setDangerError(data.detail || 'Failed to delete account');
      }
    } catch (e) {
      setDangerError('An error occurred');
    } finally {
      setDangerLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
  };

  const inputStyle = {
    width: '100%', padding: '10px 14px', borderRadius: '10px',
    border: `1px solid ${c.inputBorder}`, background: c.inputBg,
    color: c.text, fontSize: '14px', fontFamily: 'inherit', outline: 'none',
  };

  const btnPrimary = {
    padding: '10px 20px', borderRadius: '10px', border: 'none',
    background: c.accent, color: '#FFF', fontSize: '14px',
    fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
    transition: 'all 0.2s',
  };

  const btnSecondary = {
    padding: '10px 20px', borderRadius: '10px',
    border: `1px solid ${c.border}`, background: 'transparent',
    color: c.textSecondary, fontSize: '14px', fontWeight: '500',
    cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s',
  };

  const sectionStyle = {
    padding: '28px', borderRadius: '16px',
    background: c.bgCard, border: `1px solid ${c.border}`,
    marginBottom: '20px',
  };

  const labelStyle = {
    fontSize: '13px', fontWeight: '600', color: c.textSecondary,
    marginBottom: '6px', display: 'block',
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: c.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: c.textMuted, fontFamily: "'Outfit', sans-serif" }}>Loading...</div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh', background: c.bg, color: c.text,
      fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif",
    }}>
      {/* Nav */}
      <nav style={{
        position: 'sticky', top: 0, zIndex: 100, padding: '0 24px',
        background: isDarkMode ? 'rgba(8,9,11,0.92)' : 'rgba(255,255,255,0.92)',
        backdropFilter: 'blur(20px)',
        borderBottom: `1px solid ${c.border}`,
      }}>
        <div style={{ maxWidth: '700px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '64px' }}>
          <button onClick={() => navigate('/app')} style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            background: 'none', border: 'none', color: c.textSecondary,
            fontSize: '14px', fontWeight: '500', cursor: 'pointer', fontFamily: 'inherit',
          }}>
            <ArrowLeft size={16} /> Back to App
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <img src={isDarkMode ? logoSourceInverted : logoSource} alt="DataQuad" style={{ width: '24px', height: '24px' }} />
            <span style={{ fontSize: '15px', fontWeight: '600' }}>Account Settings</span>
          </div>
          <button onClick={toggleTheme} style={{ background: 'none', border: 'none', color: c.textMuted, cursor: 'pointer', padding: '6px', display: 'flex' }}>
            {isDarkMode ? <Sun size={16} /> : <Moon size={16} />}
          </button>
        </div>
      </nav>

      <div style={{ maxWidth: '700px', margin: '0 auto', padding: '40px 24px 80px' }}>

        {/* ============================================================
            PROFILE SECTION
            ============================================================ */}
        <div style={sectionStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '24px' }}>
            <User size={18} style={{ color: c.accent }} />
            <h2 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>Profile</h2>
          </div>

          {/* Avatar + Email */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
            {profile?.picture ? (
              <img src={profile.picture} alt="" style={{ width: '48px', height: '48px', borderRadius: '50%' }} />
            ) : (
              <div style={{
                width: '48px', height: '48px', borderRadius: '50%',
                background: c.accentGradient, display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#FFF', fontSize: '20px', fontWeight: '700',
              }}>
                {(profile?.name || profile?.email || '?')[0].toUpperCase()}
              </div>
            )}
            <div>
              <div style={{ fontSize: '16px', fontWeight: '600' }}>{profile?.name}</div>
              <div style={{ fontSize: '13px', color: c.textMuted }}>{profile?.email}</div>
              {profile?.is_google_auth && (
                <div style={{ fontSize: '11px', color: c.textMuted, marginTop: '2px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Shield size={10} /> Signed in with Google
                </div>
              )}
            </div>
          </div>

          {/* Edit Name */}
          <div>
            <label style={labelStyle}>Display Name</label>
            {editingName ? (
              <div style={{ display: 'flex', gap: '8px' }}>
                <input value={newName} onChange={e => setNewName(e.target.value)} style={{ ...inputStyle, flex: 1 }} />
                <button onClick={handleUpdateName} disabled={nameLoading} style={btnPrimary}>
                  {nameLoading ? '...' : 'Save'}
                </button>
                <button onClick={() => { setEditingName(false); setNewName(profile?.name || ''); }} style={btnSecondary}>Cancel</button>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '15px' }}>{profile?.name}</span>
                <button onClick={() => setEditingName(true)} style={{ ...btnSecondary, padding: '6px 14px', fontSize: '13px' }}>Edit</button>
                {nameSuccess && <span style={{ fontSize: '13px', color: c.success, display: 'flex', alignItems: 'center', gap: '4px' }}><Check size={14} /> Saved</span>}
              </div>
            )}
          </div>
        </div>

        {/* ============================================================
            PASSWORD SECTION (non-Google only)
            ============================================================ */}
        {!profile?.is_google_auth && (
          <div style={sectionStyle}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Lock size={18} style={{ color: c.accent }} />
                <h2 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>Password</h2>
              </div>
              <button onClick={() => setShowPasswordSection(!showPasswordSection)} style={{ ...btnSecondary, padding: '6px 14px', fontSize: '13px' }}>
                {showPasswordSection ? 'Cancel' : 'Change Password'}
              </button>
            </div>

            {showPasswordSection && (
              <div style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div>
                  <label style={labelStyle}>Current Password</label>
                  <div style={{ position: 'relative' }}>
                    <input type={showCurrentPw ? 'text' : 'password'} value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} style={inputStyle} />
                    <button onClick={() => setShowCurrentPw(!showCurrentPw)} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: c.textMuted, cursor: 'pointer' }}>
                      {showCurrentPw ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>New Password</label>
                  <div style={{ position: 'relative' }}>
                    <input type={showNewPw ? 'text' : 'password'} value={newPassword} onChange={e => setNewPassword(e.target.value)} style={inputStyle} placeholder="At least 8 characters" />
                    <button onClick={() => setShowNewPw(!showNewPw)} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: c.textMuted, cursor: 'pointer' }}>
                      {showNewPw ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>Confirm New Password</label>
                  <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} style={inputStyle} />
                </div>
                {pwMessage && <p style={{ fontSize: '13px', color: pwMessage.includes('success') ? c.success : c.danger, margin: 0 }}>{pwMessage}</p>}
                <button onClick={handleChangePassword} disabled={pwLoading} style={{ ...btnPrimary, width: 'fit-content' }}>
                  {pwLoading ? 'Changing...' : 'Update Password'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* ============================================================
            SUBSCRIPTION SECTION
            ============================================================ */}
        <div style={sectionStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
            <CreditCard size={18} style={{ color: c.accent }} />
            <h2 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>Subscription</h2>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <span style={{ fontSize: '14px', color: c.textMuted }}>Plan</span>
            <span style={{ fontSize: '14px', fontWeight: '600', textTransform: 'capitalize' }}>
              {profile?.subscription_plan || 'None'}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <span style={{ fontSize: '14px', color: c.textMuted }}>Status</span>
            <span style={{
              fontSize: '13px', fontWeight: '600', textTransform: 'capitalize',
              color: profile?.subscription_status === 'active' ? c.success
                : profile?.subscription_status === 'trialing' ? c.accent : c.textMuted,
            }}>
              {profile?.subscription_status || 'Inactive'}
            </span>
          </div>
          {profile?.trial_end_date && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <span style={{ fontSize: '14px', color: c.textMuted }}>Trial ends</span>
              <span style={{ fontSize: '14px' }}>{new Date(profile.trial_end_date).toLocaleDateString()}</span>
            </div>
          )}

          <div style={{ display: 'flex', gap: '10px', marginTop: '20px', flexWrap: 'wrap' }}>
            <button onClick={() => navigate('/subscription')} style={btnPrimary}>
              {profile?.subscription_plan ? 'Manage Plan' : 'Choose a Plan'}
            </button>
            {profile?.paddle_customer_id && (
              <a
                href={PADDLE_ENVIRONMENT === 'sandbox' ? 'https://sandbox-customer-portal.paddle.com' : 'https://customer-portal.paddle.com'}
                target="_blank" rel="noopener noreferrer"
                style={{ ...btnSecondary, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                Billing Portal <ChevronRight size={14} />
              </a>
            )}
          </div>
        </div>

        {/* ============================================================
            ACCOUNT SECTION
            ============================================================ */}
        <div style={sectionStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
            <Shield size={18} style={{ color: c.textSecondary }} />
            <h2 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>Account</h2>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <span style={{ fontSize: '14px', color: c.textMuted }}>Member since</span>
            <span style={{ fontSize: '14px' }}>{profile?.created_at ? new Date(profile.created_at).toLocaleDateString() : '—'}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <span style={{ fontSize: '14px', color: c.textMuted }}>Projects</span>
            <span style={{ fontSize: '14px' }}>{profile?.project_count ?? 0}</span>
          </div>

          <button onClick={handleLogout} style={{
            ...btnSecondary, marginTop: '16px',
            display: 'flex', alignItems: 'center', gap: '6px',
          }}>
            <LogOut size={14} /> Sign Out
          </button>
        </div>

        {/* ============================================================
            DANGER ZONE
            ============================================================ */}
        <div style={{
          ...sectionStyle,
          background: c.dangerBg, border: `1px solid ${c.dangerBorder}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
            <AlertTriangle size={18} style={{ color: c.danger }} />
            <h2 style={{ fontSize: '18px', fontWeight: '600', margin: 0, color: c.danger }}>Danger Zone</h2>
          </div>

          {/* Deactivate */}
          <div style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: `1px solid ${c.dangerBorder}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
              <div>
                <div style={{ fontSize: '15px', fontWeight: '600', marginBottom: '4px' }}>Deactivate Account</div>
                <div style={{ fontSize: '13px', color: c.textSecondary, maxWidth: '400px' }}>
                  Temporarily disable your account. Your data is kept for 90 days.
                  Log back in within 90 days to reactivate.
                </div>
              </div>
              <button onClick={() => { setShowDeactivate(!showDeactivate); setShowDelete(false); setDangerError(''); }} style={{
                ...btnSecondary, borderColor: c.dangerBorder, color: c.danger, fontSize: '13px', padding: '8px 16px',
              }}>
                <Pause size={14} style={{ marginRight: '4px' }} /> Deactivate
              </button>
            </div>
            {showDeactivate && (
              <div style={{ marginTop: '16px', padding: '16px', borderRadius: '10px', background: isDarkMode ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.8)' }}>
                {!profile?.is_google_auth && (
                  <div style={{ marginBottom: '12px' }}>
                    <label style={labelStyle}>Confirm your password</label>
                    <input type="password" value={dangerPassword} onChange={e => setDangerPassword(e.target.value)} style={inputStyle} />
                  </div>
                )}
                {dangerError && <p style={{ fontSize: '13px', color: c.danger, margin: '0 0 12px' }}>{dangerError}</p>}
                <button onClick={handleDeactivate} disabled={dangerLoading} style={{
                  ...btnPrimary, background: c.danger,
                }}>
                  {dangerLoading ? 'Processing...' : 'Confirm Deactivation'}
                </button>
              </div>
            )}
          </div>

          {/* Delete */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
              <div>
                <div style={{ fontSize: '15px', fontWeight: '600', marginBottom: '4px' }}>Delete Account</div>
                <div style={{ fontSize: '13px', color: c.textSecondary, maxWidth: '400px' }}>
                  Permanently delete your account and all data. This action cannot be undone.
                </div>
              </div>
              <button onClick={() => { setShowDelete(!showDelete); setShowDeactivate(false); setDangerError(''); }} style={{
                ...btnSecondary, borderColor: c.dangerBorder, color: c.danger, fontSize: '13px', padding: '8px 16px',
              }}>
                <Trash2 size={14} style={{ marginRight: '4px' }} /> Delete
              </button>
            </div>
            {showDelete && (
              <div style={{ marginTop: '16px', padding: '16px', borderRadius: '10px', background: isDarkMode ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.8)' }}>
                {!profile?.is_google_auth && (
                  <div style={{ marginBottom: '12px' }}>
                    <label style={labelStyle}>Confirm your password</label>
                    <input type="password" value={dangerPassword} onChange={e => setDangerPassword(e.target.value)} style={inputStyle} />
                  </div>
                )}
                <div style={{ marginBottom: '12px' }}>
                  <label style={labelStyle}>Type <strong>DELETE MY ACCOUNT</strong> to confirm</label>
                  <input value={deleteConfirmation} onChange={e => setDeleteConfirmation(e.target.value)} style={inputStyle} placeholder="DELETE MY ACCOUNT" />
                </div>
                {dangerError && <p style={{ fontSize: '13px', color: c.danger, margin: '0 0 12px' }}>{dangerError}</p>}
                <button
                  onClick={handleDelete}
                  disabled={dangerLoading || deleteConfirmation !== 'DELETE MY ACCOUNT'}
                  style={{
                    ...btnPrimary, background: c.danger,
                    opacity: deleteConfirmation !== 'DELETE MY ACCOUNT' ? 0.5 : 1,
                  }}
                >
                  {dangerLoading ? 'Deleting...' : 'Permanently Delete Account'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{ textAlign: 'center', marginTop: '32px' }}>
          <p style={{ fontSize: '12px', color: c.textMuted }}>
            DataQuad by Noble Syntax Limited · <a href="/terms" style={{ color: c.textMuted }}>Terms</a> · <a href="/privacy" style={{ color: c.textMuted }}>Privacy</a> · <a href="/refund" style={{ color: c.textMuted }}>Refunds</a>
          </p>
        </div>
      </div>

      <style>{`
        input:focus { border-color: ${c.accent} !important; box-shadow: 0 0 0 3px rgba(43,138,255,0.1) !important; }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}; border-radius: 3px; }
      `}</style>
    </div>
  );
};

export default AccountSettings;